export interface WorkplanLineItem {
  id: string
  name: string
  categoryName: string
  // Optional: the public share page doesn't receive catalog references.
  serviceId?: string | null
  pricingType: string
  estimatedHours: number | null
  isOptional: boolean
  tasks: {
    name: string
    estimatedHours: number | null
    phase?: string
  }[]
}

const PHASE_ORDER = ['project_management', 'staging', 'execution', 'support_and_training', 'documentation']

const PHASE_LABELS: Record<string, string> = {
  project_management: 'Project Management',
  staging: 'Staging',
  execution: 'Execution',
  support_and_training: 'Support & Training',
  documentation: 'Documentation',
}

type TaskEntry = { name: string }
type ServiceGroup = { serviceName: string; tasks: TaskEntry[] }

export default function SowWorkplan({ items }: { items: WorkplanLineItem[] }) {
  if (items.length === 0) return null

  // Group tasks by phase, then by service within each phase
  const byPhase = new Map<string, ServiceGroup[]>()
  for (const item of items.filter(i => !i.isOptional)) {
    if (item.tasks.length === 0) continue
    const itemTasksByPhase = new Map<string, TaskEntry[]>()
    for (const task of item.tasks) {
      const phase = task.phase ?? 'execution'
      const list = itemTasksByPhase.get(phase) ?? []
      list.push({ name: task.name })
      itemTasksByPhase.set(phase, list)
    }
    for (const [phase, tasks] of itemTasksByPhase) {
      const groups = byPhase.get(phase) ?? []
      groups.push({ serviceName: item.name, tasks })
      byPhase.set(phase, groups)
    }
  }

  const orderedPhases = [
    ...PHASE_ORDER.filter(p => byPhase.has(p)),
    ...[...byPhase.keys()].filter(p => !PHASE_ORDER.includes(p)),
  ]

  if (orderedPhases.length === 0) return null

  return (
    <section>
      <h2 className="mb-4 text-xl font-bold text-gray-900">Statement of Work</h2>
      <div className="space-y-6">
        {orderedPhases.map(phase => {
          const groups = byPhase.get(phase) ?? []
          return (
            <div key={phase}>
              <div className="mb-2 border-b border-gray-200 pb-1.5">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                  {PHASE_LABELS[phase] ?? phase}
                </h3>
              </div>
              <div className="space-y-3 pl-1">
                {groups.map((group, gi) => (
                  <div key={gi}>
                    <p className="mb-1 text-xs font-semibold text-gray-900">{group.serviceName}</p>
                    <div className="space-y-0.5">
                      {group.tasks.map((task, idx) => (
                        <div key={idx} className="px-1 py-1">
                          <span className="text-sm text-gray-700">{task.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
